import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  CompanyComment,
  CompanyProfile,
} from '../../interfaces/company-profile';
import { CompanyProfileService } from '../../services/company-profile.service';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-company-overview',
  templateUrl: './company-overview.component.html',
  styleUrls: ['./company-overview.component.css'],
})
export class CompanyOverviewComponent implements OnChanges {
  @Input() companyProfile: CompanyProfile | null = null;

  starsArray: string[] = [];
  comments: CompanyComment[] = [];
  isLoadingComments: boolean = false;
  transformedBenefits: SafeHtml[] = [];
  transformedVision: SafeHtml[] = [];

  // Modal
  showReviewModal: boolean = false;
  reviewContent: string = '';
  reviewRate: number = 0;
  hoveredStar: number = 0;
  isSubmitting: boolean = false;
  submitError: string = '';
  submitSuccess: boolean = false;

  constructor(
    private companyProfileService: CompanyProfileService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyProfile'] && this.companyProfile) {
      this.starsArray = this.getStarsArray(this.companyProfile.rate);
      this.loadComments();
    }
    if (this.companyProfile?.benefits) {
      this.transformedBenefits = [
        this.transformBenefitsHtml(this.companyProfile.benefits),
      ];
    }
    if (this.companyProfile?.vision) {
      this.transformedVision = [
        this.transformBenefitsHtml(this.companyProfile.vision),
      ];
    }
  }

  getuserimage(comment: CompanyComment): string {
    return comment?.userPicture
      ? environment.apiImagePath + comment.userPicture
      : '../../../assets/img/company/compnay-header.jpg';
  }

  getReplyAuthorImage(reply: any): string {
    return reply?.authorPicture
      ? environment.apiImagePath + reply.authorPicture
      : '../../../assets/img/company/compnay-header.jpg';
  }

  loadComments(): void {
    if (!this.companyProfile?.userId) return;
    this.isLoadingComments = true;
    this.companyProfileService
      .getCompanyComments(this.companyProfile.userId)
      .subscribe({
        next: (data) => {
          this.comments = data.content;
          this.isLoadingComments = false;
        },
        error: () => {
          this.isLoadingComments = false;
        },
      });
  }

  openReviewModal(): void {
    this.showReviewModal = true;
    this.reviewContent = '';
    this.reviewRate = 0;
    this.hoveredStar = 0;
    this.submitError = '';
    this.submitSuccess = false;
  }

  closeReviewModal(): void {
    this.showReviewModal = false;
  }

  setRate(star: number): void {
    this.reviewRate = star;
  }

  setHovered(star: number): void {
    this.hoveredStar = star;
  }

  clearHovered(): void {
    this.hoveredStar = 0;
  }

  transformBenefitsHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    let result = '';
    doc.querySelectorAll('p').forEach((p) => {
      const text = p.textContent?.trim() ?? '';
      if (text) {
        result += `<p style="color:#5e6670;font-size:1rem;line-height:1.6;margin-bottom:10px;">${text}</p>`;
      }
    });
    doc.querySelectorAll('li').forEach((li) => {
      li.querySelectorAll('.ql-ui').forEach((el) => el.remove());
      const text = li.textContent?.trim() ?? '';
      if (text) {
        result += `
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;color:#5e6670;font-size:1rem;line-height:1.6;">
            <span style="width:5px;height:5px;min-width:5px;border-radius:50%;background:#5e6670;display:inline-block;flex-shrink:0;"></span>
            ${text}
          </div>
        `;
      }
    });

    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

  getStarClass(star: number): string {
    const active = this.hoveredStar || this.reviewRate;
    return star <= active ? 'bi-star-fill active' : 'bi-star';
  }

  submitReview(): void {
    if (!this.reviewContent.trim()) {
      this.submitError = 'Please write your review.';
      return;
    }
    if (this.reviewRate === 0) {
      this.submitError = 'Please select a rating.';
      return;
    }
    if (!this.companyProfile?.userId) return;

    this.isSubmitting = true;
    this.submitError = '';

    this.companyProfileService
      .addCompanyComment(
        this.companyProfile.userId,
        this.reviewContent.trim(),
        this.reviewRate,
      )
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitSuccess = true;
          setTimeout(() => this.closeReviewModal(), 1500);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.submitError =
            err?.error?.message || 'Something went wrong. Please try again.';
        },
      });
  }

  getStarsArray(rate: number): string[] {
    const stars: string[] = [];
    const fullStars = Math.floor(rate);
    const hasHalf = rate % 1 >= 0.5;
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) stars.push('bi-star-fill');
      else if (i === fullStars && hasHalf) stars.push('bi-star-half');
      else stars.push('bi-star');
    }
    return stars;
  }

  getCommentStars(rate: number): string[] {
    return this.getStarsArray(rate);
  }

  getUserInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  openLink(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }
}
